import { useState } from 'react';
import clsx from 'clsx';

import { Checkbox, Collapse, Icon, IconButton, Switch } from '@atom/mui';
import colors from '@atom/styles/colors';
import workOrderStatusUtilities from '@atom/utilities/workOrderStatusUtilities';

import MapDrawerLayerIcon from './MapDrawerLayerIcon';
import MapDrawerLayersWorkTemplateFilter from './MapDrawerLayersWorkTemplateFilter';
import MapDrawerWorkTemplateFolderFilter from './MapDrawerWorkTemplateFolderFilter';

import classes from './mapDrawerLayers.module.css';

const ALL_WORK_STATUS_IDS = [
  workOrderStatusUtilities.REQUESTED_STATUS_ID,
  workOrderStatusUtilities.UNASSIGNED_STATUS_ID,
  workOrderStatusUtilities.ASSIGNED_STATUS_ID,
  workOrderStatusUtilities.IN_PROGRESS_STATUS_ID,
  workOrderStatusUtilities.COMPLETED_STATUS_ID,
  workOrderStatusUtilities.IN_REVIEW_STATUS_ID,
  workOrderStatusUtilities.CLOSED_STATUS_ID,
];

interface Props {
  onUpdate: (isInputChecked: boolean, workStatusIds: Set<any>) => any;
  onWorkTemplatesUpdate: (toggled: boolean, selected: Set<any>) => any;
  onWorkTemplateFoldersUpdate: (toggled: boolean, selected: Set<any>) => any;
  toggled: boolean;
  workStatusIds: any;
  workTemplateIds: any;
  workTemplateFolderIds?: any;
}

const MapDrawerLayersWorkSection = ({
  onUpdate,
  onWorkTemplatesUpdate,
  onWorkTemplateFoldersUpdate,
  toggled,
  workStatusIds,
  workTemplateIds,
  workTemplateFolderIds,
}: Props) => {
  const [expanded, setExpanded] = useState<boolean>(true);

  const toggleWorkStatusLayer = workStatusId => {
    const worStatusIdsSet = new Set([...workStatusIds]);
    if (worStatusIdsSet.has(workStatusId)) {
      worStatusIdsSet.delete(workStatusId);
    } else {
      worStatusIdsSet.add(workStatusId);
    }

    onUpdate(toggled, worStatusIdsSet);
  };

  const selectAllWorkLayers = () => {
    onUpdate(toggled, new Set([...ALL_WORK_STATUS_IDS]));
  };

  const deselectAllWorkLayers = () => {
    onUpdate(toggled, new Set([]));
  };

  const allWorkLayersSelected = ALL_WORK_STATUS_IDS.every(status =>
    workStatusIds.has(status),
  );

  const noWorkLayersSelected = !workStatusIds.size;

  return (
    <div className={classes.sectionContainer}>
      <div className={classes.sectionHeaderContainer}>
        <Switch
          label={
            <div className={classes.toggleLabel}>
              <div className={classes.sectionHeaderIcon}>
                <MapDrawerLayerIcon name="work_outlined" />
              </div>
              <div>Work</div>
            </div>
          }
          checked={toggled}
          onChange={() => {
            onUpdate(!toggled, workStatusIds);
          }}
        />
        <IconButton edge="end" onClick={() => setExpanded(!expanded)}>
          <Icon>{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
        </IconButton>
      </div>
      <div className={clsx(classes.sectionFilters, classes.expanded)}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <div className={classes.sectionLabel}>Status</div>
          <div className={classes.selectAllContainer}>
            <div
              onClick={() => toggled && selectAllWorkLayers()}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || allWorkLayersSelected) && classes.disabled,
              )}
            >
              Select All
            </div>
            <div className={classes.selectAllDivider} />
            <div
              onClick={() => toggled && deselectAllWorkLayers()}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || noWorkLayersSelected) && classes.disabled,
              )}
            >
              Clear All
            </div>
          </div>
          <div className={classes.sectionFilterRow}>
            <div className={classes.sectionFilterCol}>
              <Checkbox
                edge="start"
                checked={workStatusIds.has(
                  workOrderStatusUtilities.REQUESTED_STATUS_ID,
                )}
                onChange={() =>
                  toggleWorkStatusLayer(
                    workOrderStatusUtilities.REQUESTED_STATUS_ID,
                  )
                }
                disabled={!toggled}
                data-cy="dashboardWorkFilterRequested"
              />
              <MapDrawerLayerIcon name="work" color={colors.status.requested} />
              <span className={classes.sectionFilterRowText}>Requested</span>
            </div>
            <div className={classes.sectionFilterCol}>
              <Checkbox
                edge="start"
                checked={workStatusIds.has(
                  workOrderStatusUtilities.UNASSIGNED_STATUS_ID,
                )}
                onChange={() =>
                  toggleWorkStatusLayer(
                    workOrderStatusUtilities.UNASSIGNED_STATUS_ID,
                  )
                }
                disabled={!toggled}
                data-cy="dashboardWorkFilterUnassigned"
              />
              <MapDrawerLayerIcon
                name="work"
                color={colors.status.unassigned}
              />
              <span className={classes.sectionFilterRowText}>Unassigned</span>
            </div>
          </div>
          <div className={classes.sectionFilterRow}>
            <div className={classes.sectionFilterCol}>
              <Checkbox
                edge="start"
                checked={workStatusIds.has(
                  workOrderStatusUtilities.ASSIGNED_STATUS_ID,
                )}
                onChange={() =>
                  toggleWorkStatusLayer(
                    workOrderStatusUtilities.ASSIGNED_STATUS_ID,
                  )
                }
                disabled={!toggled}
                data-cy="dashboardWorkFilterAssigned"
              />
              <MapDrawerLayerIcon name="work" color={colors.status.assigned} />
              <span className={classes.sectionFilterRowText}>Assigned</span>
            </div>
            <div className={classes.sectionFilterCol}>
              <Checkbox
                edge="start"
                checked={workStatusIds.has(
                  workOrderStatusUtilities.IN_PROGRESS_STATUS_ID,
                )}
                onChange={() =>
                  toggleWorkStatusLayer(
                    workOrderStatusUtilities.IN_PROGRESS_STATUS_ID,
                  )
                }
                disabled={!toggled}
                data-cy="dashboardWorkFilterInProgress"
              />
              <MapDrawerLayerIcon
                name="work"
                color={colors.status.inprogress}
              />
              <span className={classes.sectionFilterRowText}>In Progress</span>
            </div>
          </div>
          <div className={classes.sectionFilterRow}>
            <div className={classes.sectionFilterCol}>
              <Checkbox
                edge="start"
                checked={workStatusIds.has(
                  workOrderStatusUtilities.COMPLETED_STATUS_ID,
                )}
                onChange={() =>
                  toggleWorkStatusLayer(
                    workOrderStatusUtilities.COMPLETED_STATUS_ID,
                  )
                }
                disabled={!toggled}
                data-cy="dashboardWorkFilterCompleted"
              />
              <MapDrawerLayerIcon name="work" color={colors.status.completed} />
              <span className={classes.sectionFilterRowText}>Completed</span>
            </div>
            <div className={classes.sectionFilterCol}>
              <Checkbox
                edge="start"
                checked={workStatusIds.has(
                  workOrderStatusUtilities.IN_REVIEW_STATUS_ID,
                )}
                onChange={() =>
                  toggleWorkStatusLayer(
                    workOrderStatusUtilities.IN_REVIEW_STATUS_ID,
                  )
                }
                disabled={!toggled}
                data-cy="dashboardWorkFilterInReview"
              />
              <MapDrawerLayerIcon name="work" color={colors.status.inReview} />
              <span className={classes.sectionFilterRowText}>In Review</span>
            </div>
          </div>
          <div className={classes.sectionFilterRow}>
            <Checkbox
              edge="start"
              checked={workStatusIds.has(
                workOrderStatusUtilities.CLOSED_STATUS_ID,
              )}
              onChange={() =>
                toggleWorkStatusLayer(workOrderStatusUtilities.CLOSED_STATUS_ID)
              }
              disabled={!toggled}
              data-cy="dashboardWorkFilterClosed"
            />
            <MapDrawerLayerIcon name="work" color={colors.status.closed} />
            <span className={classes.sectionFilterRowText}>Closed</span>
          </div>
          <div className={classes.filterContainer}>
            <MapDrawerWorkTemplateFolderFilter
              onWorkTemplateFoldersUpdate={selectedIds =>
                onWorkTemplateFoldersUpdate(toggled, selectedIds)
              }
              workTemplateFolderIds={workTemplateFolderIds}
              toggled={toggled}
            />
          </div>
          <div className={classes.filterContainer}>
            <MapDrawerLayersWorkTemplateFilter
              onWorkTemplatesUpdate={selectedIds =>
                onWorkTemplatesUpdate(toggled, selectedIds)
              }
              workTemplateIds={workTemplateIds}
              toggled={toggled}
            />
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default MapDrawerLayersWorkSection;
